import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, AuthUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({ user: null, loading: true });

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/**
 * Auth context provider that wraps the entire app.
 * Listens for Amplify Hub auth events so auth state updates after OAuth redirects.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
          getCurrentUser()
            .then((u) => { setUser(u); setLoading(false); })
            .catch(() => { setUser(null); setLoading(false); });
          break;
        case 'signedOut':
          setUser(null);
          setLoading(false);
          break;
        case 'signInWithRedirect_failure':
          setUser(null);
          setLoading(false);
          break;
      }
    });

    getCurrentUser()
      .then((u) => { setUser(u); setLoading(false); })
      .catch(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('code')) {
          setLoading(false);
        }
      });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => setLoading(false), 15000);
    return () => clearTimeout(timeout);
  }, [loading]);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
